import parse from 'html-react-parser'

export const formatSaleYear = (date: string | null | undefined): string => {
  if (!date) return '';
  try {
    return new Date(date).getFullYear().toString();
  } catch {
    return '';
  }
};

interface PageCountOptions {
  pages: number | null | undefined;
  includeText?: boolean;
}

export const formatPageCount = ({ pages, includeText = true }: PageCountOptions): string => {
  if (!pages || pages < 10) {
    return '';
  }
  return includeText ? `${pages} pages` : `${pages}`;
};
export const parseContent = (content: string | null) => {
  if (!content) return null;
  return parse(content);
};

export const getBookContent = (
  positioning?: string | null | undefined,
  flapcopy?: string | null | undefined,
  keynote?: string | null | undefined,
  jacketquotes?: string | null | undefined
): string => {
  // First priority: Flapcopy
  if (flapcopy) {
    return flapcopy;
  }
  // Second priority: Positioning and/or Keynote combined
  if (positioning || keynote) {
    return [positioning, keynote]
      .filter((content): content is string => !!content) 
      .join('\n\n')
      .trim();
  }
  // Third priority: Jacket quotes
  if (jacketquotes) {
    return jacketquotes;
  }
  return '';
}

export function generateBookSlug(title: string, author: string, urnNumber: string): string {
  if (!title || !author || !urnNumber) {
    console.error('Missing required data for slug generation:', { title, author, urnNumber });
    return '';
  }

  const cleanStr = (str: string) => str
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();

  const slugTitle = cleanStr(title);
  const slugAuthor = cleanStr(author);
  return `${slugTitle}-${slugAuthor}-${urnNumber}`;
}

export function extractWorkUrn(slug: string | undefined): string {
  if (!slug) return '';
  // If it's a number only
  if (/^\d+$/.test(slug)) {
    return `zilu:work:${slug}`;
  }
  
  // If it contains dashes (slug format)
  if (slug.includes('-')) {
    const parts = slug.split('-');
    const lastPart = parts[parts.length - 1];
    if (lastPart && /^\d+$/.test(lastPart)) {
      return `zilu:work:${lastPart}`;
    }
  }
  
  // If it's already a work URN
  if (slug.startsWith('zilu:work:')) {
    return slug;
  }
  return '';
}

export function extractUrnNumber(workUrn: string | undefined): string {
  if (!workUrn) return '';
  const parts = workUrn.split(':');
  return parts.length > 2 ? parts[2] : '';
}



export function formatRating(rating?: number): string {
  if (!rating) return ""
  // Handle 1-999: Display as is
  if (rating < 1000) {
    return rating.toString() + ' ratings';
  }
  
  // Handle 1,000-9,999: Add commas
  if (rating < 10000) {
    return rating.toLocaleString() + ' ratings';
  }
  
  // Handle 10,000-999,499: Display as thousands with 'k'
  if (rating < 999500) {
    const thousands = Math.floor(rating / 1000);
    const remainder = rating % 1000;
    
    // If there's a remainder and it's significant, include a decimal
    if (remainder >= 100) {
      const decimal = Math.floor(remainder / 100);
      return `${thousands}.${decimal}k ratings`;
    }
    
    return `${thousands}k ratings`;
  }
  
  // Handle 999,500+: Display as millions with 'M'
  const millions = rating / 1000000;
  
  // Round to 1 decimal place
  return `${millions.toFixed(1)}m ratings`;
}

export function isbnToAsin(isbn: string): string {
  // Remove any hyphens, spaces, or non-alphanumeric characters
  const cleanIsbn = isbn.replace(/[^\w]/g, '');
  
  // If ISBN-13, convert to ISBN-10
  if (cleanIsbn.length === 13 && cleanIsbn.startsWith('978')) {
      // Take the 9 digits after the 978 prefix
      const base = cleanIsbn.slice(3, 12);
      
      // Calculate check digit
      let sum = 0;
      for (let i = 0; i < base.length; i++) {
          sum += (10 - i) * parseInt(base[i], 10);
      }
      
      const remainder = sum % 11;
      const checkDigit = remainder === 0 ? '0' : 
                         remainder === 1 ? 'X' : 
                         (11 - remainder).toString();
      
      return base + checkDigit;
  }
  
  // If already ISBN-10 or ASIN, return as is (preserving 'X' if present)
  return cleanIsbn.length === 10 ? cleanIsbn : cleanIsbn.slice(0, 10);
}